import {
  useDebounceFn,
  useEventListener,
  useThrottleFn,
} from '@vueuse/core'
import type { Ref } from 'vue'
import { ref } from 'vue'

export interface UseElementHoverOptions {
  delayEnter?: number;
  delayLeave?: number;
  /** 超时后 hover 状态会重置为 false */
  timeout?: number;
  ignoreEl?: string[];
  keepAlive?: string[];
}

export function useElementHover(el: Element | Ref<Element | undefined>, options: UseElementHoverOptions = {}) {
  const {
    delayEnter,
    delayLeave,
    timeout,
    ignoreEl = [],
    keepAlive = [],
  } = options

  const isHover = ref(false)
  let timer: ReturnType<typeof setTimeout> | undefined

  const handleTimeout = useDebounceFn((keepAliveOnTimeout?: boolean) => {
    if (!isHover.value || keepAliveOnTimeout || !timeout) {
      return
    }

    isHover.value = false
  }, timeout || 0)

  const toggle = (entering: boolean, keepAliveOnToggle?: boolean) => {
    const delay = entering ? delayEnter : delayLeave

    if (timer) {
      clearTimeout(timer)
      timer = undefined
    }

    if (delay) {
      timer = setTimeout(() => {
        isHover.value = entering
        handleTimeout(keepAliveOnToggle)
      }, delay)
    } else {
      isHover.value = entering
      handleTimeout(keepAliveOnToggle)
    }
  }

  const closeMouseEnter = useEventListener(el, 'mouseenter', () => toggle(true), { passive: true })

  const closeMouseMove = useEventListener(el, 'mousemove', useThrottleFn((e: MouseEvent) => {
    if (keepAlive.length && (e?.target as HTMLElement)?.closest(keepAlive.join(','))) {
      return toggle(true, true)
    }
    toggle(true)
  }, 100), { passive: true })

  const closeMouseLeave = useEventListener(el, 'mouseleave', (e: MouseEvent) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (ignoreEl.length && (e?.toElement as HTMLElement)?.closest(ignoreEl.join(','))) {
      return
    }

    toggle(false)
  }, { passive: true })

  const handleClose = () => {
    closeMouseEnter()
    closeMouseMove()
    closeMouseLeave()
  }

  return { isHover, close: handleClose, toggle }
}
